import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Renders a horizontal SVG tree of the user's downline.
 * Auto-layouts node positions level-by-level.
 */
function NetworkTreeSVG({ root }) {
  if (!root) return null;
  const NODE_W = 150;
  const NODE_H = 56;
  const X_GAP = 24;
  const Y_GAP = 90;

  // First pass: assign x positions to leaves, then average up.
  const positioned = JSON.parse(JSON.stringify(root));
  let leafCounter = 0;
  function assignX(node, depth) {
    node.depth = depth;
    if (!node.children || node.children.length === 0) {
      node.x = leafCounter * (NODE_W + X_GAP);
      leafCounter += 1;
      return node.x;
    }
    const xs = node.children.map((c) => assignX(c, depth + 1));
    node.x = (xs[0] + xs[xs.length - 1]) / 2;
    return node.x;
  }
  assignX(positioned, 0);

  const flat = [];
  function flatten(node, parent) {
    flat.push({ ...node, parentX: parent?.x, parentY: parent ? parent.depth * (NODE_H + Y_GAP) + NODE_H : null });
    (node.children || []).forEach((c) => flatten(c, node));
  }
  flatten(positioned, null);

  const width = Math.max(leafCounter * (NODE_W + X_GAP), NODE_W);
  const height = (Math.max(...flat.map((n) => n.depth)) + 1) * (NODE_H + Y_GAP);

  return (
    <div className="overflow-x-auto" data-testid="network-tree-svg-wrap">
      <svg width={width + 20} height={height + 20} data-testid="network-tree-svg">
        {flat.map((n, i) =>
          n.parentX != null ? (
            <path
              key={`l-${i}`}
              d={`M${n.parentX + NODE_W / 2},${n.parentY} C${n.parentX + NODE_W / 2},${n.parentY + 30} ${n.x + NODE_W / 2},${n.depth * (NODE_H + Y_GAP) - 30} ${n.x + NODE_W / 2},${n.depth * (NODE_H + Y_GAP)}`}
              fill="none"
              stroke="#D4AF37"
              strokeOpacity="0.45"
              strokeWidth="1"
            />
          ) : null
        )}
        {flat.map((n, i) => {
          const y = n.depth * (NODE_H + Y_GAP);
          return (
            <g key={`n-${i}`} transform={`translate(${n.x}, ${y})`} data-testid={`tree-node-${n.user_id}`}>
              <rect
                width={NODE_W}
                height={NODE_H}
                rx={4}
                fill="#141414"
                stroke={n.active ? "#D4AF37" : "rgba(255,255,255,0.12)"}
                strokeWidth={n.depth === 0 ? 1.5 : 1}
              />
              <text x={NODE_W / 2} y={20} textAnchor="middle" fill="#F5F5F5" fontFamily="Manrope" fontSize="11" fontWeight="600">
                {(n.name || "").length > 18 ? (n.name || "").slice(0, 16) + "…" : n.name}
              </text>
              <text x={NODE_W / 2} y={36} textAnchor="middle" fill="#A3A3A3" fontFamily="Manrope" fontSize="10">
                ID {n.user_id}
              </text>
              <text x={NODE_W / 2} y={50} textAnchor="middle" fill={n.active ? "#D4AF37" : "#666"} fontFamily="Manrope" fontSize="9" letterSpacing="0.1em">
                {n.active ? (n.package || "ACTIVE").toUpperCase() : "INACTIVE"}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function Network() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tree, setTree] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/network");
        setData(r.data);
        const t = await api.get("/me/network-tree?depth=3");
        setTree(t.data.tree);
      } catch (err) {
        console.warn("Network data load failed:", err?.message);
      }
    })();
  }, []);

  const shareLink = `${window.location.origin}/register?ref=${user?.user_id}`;

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Network</div>
        <h1 className="font-display text-4xl mt-1">My Downline</h1>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="lt-card p-5">
          <div className="overline">Direct referrals</div>
          <div className="font-display text-4xl tabular mt-2" data-testid="direct-count">{data?.total_directs || 0}</div>
        </div>
        <div className="lt-card p-5">
          <div className="overline">Levels unlocked</div>
          <div className="font-display text-4xl tabular mt-2">
            {(data?.levels || []).filter((l) => l.unlocked).length} / 11
          </div>
        </div>
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Level Unlock Map</div>
        <div className="overflow-x-auto -mx-1">
        <table className="lt-table">
          <thead>
            <tr><th>Level</th><th>%</th><th>Required directs</th><th>Members</th><th>Status</th></tr>
          </thead>
          <tbody>
            {(data?.levels || []).map((l) => (
              <tr key={l.level} data-testid={`level-${l.level}`}>
                <td className="font-display text-lg">L{l.level}</td>
                <td className="tabular gold-text">{l.pct.toFixed(2)}%</td>
                <td className="tabular">{l.required_directs}</td>
                <td className="tabular">{l.count}</td>
                <td>
                  <span className={`text-xs uppercase tracking-widest ${l.unlocked ? "gold-text" : "text-neutral-500"}`}>
                    {l.unlocked ? "Unlocked" : "Locked"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Direct Members</div>
        <div className="overflow-x-auto -mx-1">
        <table className="lt-table">
          <thead><tr><th>User ID</th><th>Name</th><th>Package</th><th>Joined</th></tr></thead>
          <tbody>
            {(data?.directs || []).map((d) => (
              <tr key={d.user_id}>
                <td className="tabular">{d.user_id}</td>
                <td>
                  <div data-testid={`direct-name-${d.user_id}`}>{d.full_name}</div>
                  {d.mobile && (
                    <div className="text-xs text-neutral-500 tabular mt-0.5" data-testid={`direct-mobile-${d.user_id}`}>{d.mobile}</div>
                  )}
                </td>
                <td>{d.package ? <span className="gold-text">{d.package}</span> : <span className="text-neutral-500">Inactive</span>}</td>
                <td className="text-neutral-400">{new Date(d.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {(data?.directs || []).length === 0 && (
              <tr><td colSpan={4} className="text-center text-neutral-500 py-6">Invite members using your sponsor link.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
